import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  ReactNode,
} from "react";
import {
  addDocument,
  addPage,
  addTagToDocument,
  getDocumentPages,
} from "../utils/Api";

type UploadStatus =
  | "pending"
  | "uploading"
  | "processing"
  | "complete"
  | "error";

type InsertMode = "start" | "after" | "end";

export interface UploadItem {
  id: string;
  documentId: string | null;
  documentName: string;
  mode: "create" | "add";
  files: File[];
  status: UploadStatus;
  progress: number;
  completedPages: number;
  totalPages: number;
  currentStep: string;
  error: string | null;
  createdAt: number;
  selectedTags: string[];
  selectedOrganisation: string;
  existingDocumentId?: string | undefined;
  insertMode?: InsertMode | undefined;
  insertPageNum?: number | undefined;
}

interface StartUploadConfig {
  documentName: string;
  files: File[];
  mode: "create" | "add";
  selectedTags?: string[];
  selectedOrganisation?: string;
  existingDocumentId?: string | undefined;
  insertMode?: InsertMode | undefined;
  insertPageNum?: number | undefined;
}

interface UploadContextType {
  uploads: UploadItem[];
  isWidgetVisible: boolean;
  isWidgetMinimized: boolean;
  startUpload: (config: StartUploadConfig) => string;
  dismissUpload: (uploadId: string) => void;
  dismissAllCompleted: () => void;
  toggleWidgetMinimized: () => void;
  hideWidget: () => void;
  showWidget: () => void;
}

const UploadContext = createContext<UploadContextType | undefined>(undefined);

interface UploadProviderProps {
  children: ReactNode;
}

export const UploadProvider: React.FC<UploadProviderProps> = ({ children }) => {
  const [uploads, setUploads] = useState<UploadItem[]>([]);
  const [isWidgetVisible, setIsWidgetVisible] = useState(false);
  const [isWidgetMinimized, setIsWidgetMinimized] = useState(false);
  const pollingIntervals = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const cancelledUploads = useRef<Set<string>>(new Set());

  const updateUpload = useCallback(
    (uploadId: string, updates: Partial<UploadItem>) => {
      setUploads((prev) =>
        prev.map((u) => (u.id === uploadId ? { ...u, ...updates } : u)),
      );
    },
    [],
  );

  const calculateProgress = useCallback(
    (
      pageStatuses: Record<string, string>,
      existingCompletePages: number,
    ): {
      progress: number;
      isComplete: boolean;
      completedPages: number;
      totalPages: number;
    } => {
      const statusValues: Record<string, number> = {
        "PageStatus.PENDING": 0,
        "PageStatus.EXTRACTING": 1,
        "PageStatus.STRUCTURING": 2,
        "PageStatus.COMPLETE": 3,
        "PageStatus.FAILED": 3,
        PENDING: 0,
        EXTRACTING: 1,
        STRUCTURING: 2,
        COMPLETE: 3,
        FAILED: 3,
        pending: 0,
        extracting: 1,
        structuring: 2,
        complete: 3,
        failed: 3,
      };

      const statusArray = Object.values(pageStatuses);
      if (statusArray.length === 0)
        return { progress: 0, isComplete: false, completedPages: 0, totalPages: 0 };

      const totalPages = statusArray.length - existingCompletePages;
      if (totalPages <= 0)
        return { progress: 0, isComplete: false, completedPages: 0, totalPages: 0 };

      const maxTotalProgress = totalPages * 3;
      const currentTotalProgress =
        statusArray.reduce((acc, status) => {
          return acc + (statusValues[status] ?? 0);
        }, 0) -
        3 * existingCompletePages;

      // Count fully-finished pages (COMPLETE or FAILED both map to 3),
      // excluding the pages that were already done before this upload began.
      const completedPages = Math.max(
        0,
        statusArray.reduce(
          (acc, status) => acc + ((statusValues[status] ?? 0) === 3 ? 1 : 0),
          0,
        ) - existingCompletePages,
      );

      const progress = Math.round(
        (currentTotalProgress / maxTotalProgress) * 100,
      );
      const isComplete = currentTotalProgress >= maxTotalProgress;

      return {
        progress: Math.min(progress, 100),
        isComplete,
        completedPages: Math.min(completedPages, totalPages),
        totalPages,
      };
    },
    [],
  );

  const startPolling = useCallback(
    (uploadId: string, documentId: string, existingCompletePages: number) => {
      const existingInterval = pollingIntervals.current.get(uploadId);
      if (existingInterval) {
        clearInterval(existingInterval);
      }

      const poll = async () => {
        try {
          const { data: pagesArray } = await getDocumentPages(documentId);
          const pages: Record<string, string> = {};
          for (const page of pagesArray) {
            pages[page.id] = page.status;
          }
          const { progress, isComplete, completedPages, totalPages } =
            calculateProgress(pages, existingCompletePages);

          if (isComplete) {
            updateUpload(uploadId, {
              status: "complete",
              progress: 100,
              completedPages: totalPages,
              totalPages,
              currentStep: "Complete",
            });
            const interval = pollingIntervals.current.get(uploadId);
            if (interval) {
              clearInterval(interval);
              pollingIntervals.current.delete(uploadId);
            }
          } else {
            updateUpload(uploadId, {
              progress,
              completedPages,
              totalPages,
              currentStep: `${completedPages} of ${totalPages} pages processed`,
            });
          }
        } catch (error) {
          console.error("Polling error:", error);
        }
      };

      poll();
      const interval = setInterval(poll, 1000);
      pollingIntervals.current.set(uploadId, interval);
    },
    [calculateProgress, updateUpload],
  );

  const processUpload = useCallback(
    async (upload: UploadItem) => {
      const {
        id: uploadId,
        files,
        mode,
        selectedTags,
        selectedOrganisation,
        existingDocumentId,
        insertMode,
        insertPageNum,
      } = upload;

      try {
        let documentId: string;
        let existingCompletePages = 0;

        if (cancelledUploads.current.has(uploadId)) return;

        if (mode === "create") {
          updateUpload(uploadId, {
            status: "uploading",
            currentStep: "Creating document...",
          });
          const response = await addDocument(
            upload.documentName,
            selectedOrganisation || undefined,
          );
          if (cancelledUploads.current.has(uploadId)) return;
          documentId = response.data.id;
          updateUpload(uploadId, { documentId });
        } else {
          if (!existingDocumentId) {
            throw new Error("Document ID required for adding pages");
          }
          documentId = existingDocumentId;
          updateUpload(uploadId, { documentId, status: "uploading" });

          const { data: existingPagesArray } =
            await getDocumentPages(documentId);
          if (cancelledUploads.current.has(uploadId)) return;
          existingCompletePages = existingPagesArray.length;
        }

        if (cancelledUploads.current.has(uploadId)) return;
        updateUpload(uploadId, {
          currentStep: `Uploading ${files.length} file${files.length > 1 ? "s" : ""}...`,
        });

        // Upload files one at a time, in order. The server assigns each page's
        // `page_order` from `max(page_order) + 1` (and shifts existing pages for
        // start/after inserts) — a read-then-write that races when requests
        // overlap: concurrent uploads read the same max, collide on the same
        // order, and the pages render shuffled. Now that each request
        // synchronously stages bytes to GCS before returning, a client-side
        // stagger no longer keeps them apart, so we serialize. Each page commits
        // its order before the next request reads it. Processing still runs in
        // parallel via Cloud Tasks, so this only serializes the lightweight
        // upload step, not the actual extraction work.
        //
        // "start"/"after" only define where the first file lands; every later
        // file must be inserted right after the pages we just added so the batch
        // keeps its original order. We advance the anchor by the actual number
        // of pages inserted (a PDF expands to many), reported back in the
        // response, rather than assuming one page per file.
        let effectiveMode = insertMode;
        let insertAfter = insertMode === "after" ? insertPageNum : undefined;
        for (let i = 0; i < files.length; i++) {
          if (cancelledUploads.current.has(uploadId)) return;
          const file = files[i];
          if (!file) continue;

          const response = await addPage(
            documentId,
            file,
            null,
            effectiveMode,
            insertAfter,
          );

          const insertedCount = response?.data?.ids?.length ?? 1;
          if (effectiveMode === "start") {
            // The first file's pages now occupy orders 0..insertedCount-1;
            // anchor the rest immediately after them.
            effectiveMode = "after";
            insertAfter = insertedCount;
          } else if (effectiveMode === "after") {
            insertAfter = (insertAfter ?? 0) + insertedCount;
          }
          // "end" stays "end": each append goes to the new max, in file order.
        }

        if (cancelledUploads.current.has(uploadId)) return;

        if (mode === "create" && selectedTags.length > 0) {
          updateUpload(uploadId, { currentStep: "Adding tags..." });
          for (const tagId of selectedTags) {
            if (cancelledUploads.current.has(uploadId)) return;
            try {
              await addTagToDocument(tagId, documentId);
            } catch (error) {
              console.error("Failed to add tag:", error);
            }
          }
        }

        if (cancelledUploads.current.has(uploadId)) return;

        updateUpload(uploadId, {
          status: "processing",
          currentStep: "Processing...",
        });

        // Poll page status from the DB (the shared source of truth) rather
        // than relying on the WebSocket. Per-page processing runs via Cloud
        // Tasks on arbitrary worker instances, whose in-memory WebSocket
        // broadcasts can't reach a client connected to a different instance.
        // Polling is instance-independent, so it works in every case.
        startPolling(uploadId, documentId, existingCompletePages);
      } catch (error: any) {
        if (!cancelledUploads.current.has(uploadId)) {
          console.error("Upload error:", error);
          updateUpload(uploadId, {
            status: "error",
            error: error.message || "Upload failed",
            currentStep: "Failed",
          });
        }
      }
    },
    [updateUpload, startPolling],
  );

  useEffect(() => {
    const pendingUpload = uploads.find((u) => u.status === "pending");
    if (pendingUpload) {
      processUpload(pendingUpload);
    }
  }, [uploads, processUpload]);

  useEffect(() => {
    return () => {
      pollingIntervals.current.forEach((interval) => clearInterval(interval));
      cancelledUploads.current.clear();
    };
  }, []);

  const startUpload = useCallback((config: StartUploadConfig): string => {
    const uploadId = crypto.randomUUID();
    const newUpload: UploadItem = {
      id: uploadId,
      documentId: null,
      documentName: config.documentName,
      mode: config.mode,
      files: config.files,
      status: "pending",
      progress: 0,
      completedPages: 0,
      totalPages: 0,
      currentStep: "Queued",
      error: null,
      createdAt: Date.now(),
      selectedTags: config.selectedTags || [],
      selectedOrganisation: config.selectedOrganisation || "",
      existingDocumentId: config.existingDocumentId,
      insertMode: config.insertMode,
      insertPageNum: config.insertPageNum,
    };

    setUploads((prev) => [...prev, newUpload]);
    setIsWidgetVisible(true);
    setIsWidgetMinimized(false);

    return uploadId;
  }, []);

  const dismissUpload = useCallback((uploadId: string) => {
    cancelledUploads.current.add(uploadId);

    const interval = pollingIntervals.current.get(uploadId);
    if (interval) {
      clearInterval(interval);
      pollingIntervals.current.delete(uploadId);
    }

    setUploads((prev) => prev.filter((u) => u.id !== uploadId));

    // Clean up cancelled flag after a delay to ensure any pending ops complete
    setTimeout(() => {
      cancelledUploads.current.delete(uploadId);
    }, 1000);
  }, []);

  const dismissAllCompleted = useCallback(() => {
    setUploads((prev) =>
      prev.filter((u) => u.status !== "complete" && u.status !== "error"),
    );
  }, []);

  const toggleWidgetMinimized = useCallback(() => {
    setIsWidgetMinimized((prev) => !prev);
  }, []);

  const hideWidget = useCallback(() => {
    setIsWidgetVisible(false);
  }, []);

  const showWidget = useCallback(() => {
    setIsWidgetVisible(true);
  }, []);

  useEffect(() => {
    if (uploads.length === 0) {
      setIsWidgetVisible(false);
    }
  }, [uploads.length]);

  const value: UploadContextType = {
    uploads,
    isWidgetVisible,
    isWidgetMinimized,
    startUpload,
    dismissUpload,
    dismissAllCompleted,
    toggleWidgetMinimized,
    hideWidget,
    showWidget,
  };

  return (
    <UploadContext.Provider value={value}>{children}</UploadContext.Provider>
  );
};

export const useUpload = (): UploadContextType => {
  const context = useContext(UploadContext);
  if (!context) {
    throw new Error("useUpload must be used within an UploadProvider");
  }
  return context;
};
